import type { MetaFunction } from "@remix-run/node";

export const meta: MetaFunction = () => [
  { title: "Terms of Service — Sandcastle Admin Search" },
  {
    name: "description",
    content:
      "Terms of service for the Sandcastle Admin Search Shopify app, published by Sandcastle Interactive LLC.",
  },
];

const LAST_UPDATED = "May 14, 2026";
const COMPANY = "Sandcastle Interactive LLC";
const CONTACT_EMAIL = "support@sandcastle-web.com";
const APP_NAME = "Sandcastle Admin Search";
const GOVERNING_LAW = "the State of Washington, United States";

export default function Terms() {
  return (
    <main style={pageStyle}>
      <article style={articleStyle}>
        <header>
          <h1 style={h1Style}>Terms of Service</h1>
          <p style={metaStyle}>
            Last updated: <strong>{LAST_UPDATED}</strong>
          </p>
        </header>

        <section>
          <h2 style={h2Style}>1. Acceptance</h2>
          <p>
            By installing and using {APP_NAME} (&ldquo;the app&rdquo;) on
            your Shopify store, you (&ldquo;the merchant&rdquo;,
            &ldquo;you&rdquo;) agree to these Terms of Service
            (&ldquo;Terms&rdquo;). If you do not agree, do not install the
            app and uninstall it immediately if it is already installed.
            The app is published by {COMPANY} (&ldquo;we&rdquo;,
            &ldquo;us&rdquo;).
          </p>
        </section>

        <section>
          <h2 style={h2Style}>2. The service</h2>
          <p>
            {APP_NAME} is a Shopify embedded admin app that helps merchants
            search, filter, sort, and bulk-edit products in their Shopify
            store. The exact feature set may evolve over time. We may add,
            remove, or modify features at our discretion.
          </p>
        </section>

        <section>
          <h2 style={h2Style}>3. Your responsibilities</h2>
          <p>You agree that you will:</p>
          <ul>
            <li>
              Use the app only for the lawful operation of your Shopify
              store
            </li>
            <li>Not attempt to circumvent rate limits, scrape, or abuse the service</li>
            <li>
              Not reverse-engineer, decompile, or attempt to derive the
              source code of the app except where allowed by applicable law
            </li>
            <li>
              Not resell, sublicense, or otherwise redistribute access to
              the app
            </li>
            <li>
              Comply with Shopify&rsquo;s own{" "}
              <a
                href="https://www.shopify.com/legal/terms"
                target="_blank"
                rel="noreferrer"
              >
                Terms of Service
              </a>{" "}
              and{" "}
              <a
                href="https://www.shopify.com/legal/aup"
                target="_blank"
                rel="noreferrer"
              >
                Acceptable Use Policy
              </a>
            </li>
          </ul>
        </section>

        <section>
          <h2 style={h2Style}>4. Bulk editing and destructive actions</h2>
          <p>
            The app provides bulk edit and bulk operation features (status
            changes, tag updates, deletions, etc.) that modify product data
            in your Shopify store. You are responsible for verifying the
            scope of any bulk action before confirming it. We are not
            liable for unintended changes to your catalog made through bulk
            operations you initiate.
          </p>
          <p>
            Some bulk operations (delete, archive, channel publication
            changes) are executed by Shopify&rsquo;s native admin flows
            from within the app. Those operations are governed by
            Shopify&rsquo;s confirmation and rollback policies, not ours.
          </p>
        </section>

        <section>
          <h2 style={h2Style}>5. Pricing</h2>
          <p>
            {APP_NAME} is currently offered free of charge. We reserve the
            right to introduce paid tiers in the future. If we do, we will
            give you reasonable advance notice before any charge applies to
            your installation, and you will have the option to uninstall
            without penalty.
          </p>
        </section>

        <section>
          <h2 style={h2Style}>6. Service availability</h2>
          <p>
            We aim to keep the app continuously available, but we provide
            it on an &ldquo;as-is, as-available&rdquo; basis. We do not
            guarantee uninterrupted access or specific response times. We
            may perform maintenance, deploy updates, or experience
            unforeseen outages. We are not liable for any losses you incur
            during periods of unavailability.
          </p>
        </section>

        <section>
          <h2 style={h2Style}>7. Data</h2>
          <p>
            Our handling of data collected via the app is described in our{" "}
            <a href="/privacy">Privacy Policy</a>, which is incorporated
            into these Terms by reference.
          </p>
        </section>

        <section>
          <h2 style={h2Style}>8. Intellectual property</h2>
          <p>
            All rights, title, and interest in the app &mdash; including
            its source code, design, branding, and any associated
            documentation &mdash; remain the property of {COMPANY}.
            Installing the app grants you a non-exclusive,
            non-transferable, revocable licence to use it on your Shopify
            stores. You retain all rights to your own product data.
          </p>
        </section>

        <section>
          <h2 style={h2Style}>9. Disclaimers</h2>
          <p>
            THE APP IS PROVIDED &ldquo;AS IS&rdquo; AND &ldquo;AS
            AVAILABLE&rdquo;, WITHOUT WARRANTIES OF ANY KIND, EITHER
            EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED
            WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
            PURPOSE, AND NON-INFRINGEMENT.
          </p>
        </section>

        <section>
          <h2 style={h2Style}>10. Limitation of liability</h2>
          <p>
            To the fullest extent permitted by law, {COMPANY} shall not be
            liable for any indirect, incidental, special, consequential, or
            punitive damages, including but not limited to lost profits,
            lost data, or business interruption, arising out of or in
            connection with your use of the app.
          </p>
          <p>
            Our total cumulative liability arising out of or relating to
            these Terms or the app shall not exceed one hundred US dollars
            (US $100) or the amount you paid us in the twelve months
            preceding the claim, whichever is greater.
          </p>
        </section>

        <section>
          <h2 style={h2Style}>11. Termination</h2>
          <p>
            You may terminate these Terms at any time by uninstalling the
            app from your Shopify admin. We may suspend or terminate your
            access to the app at our discretion, including but not limited
            to abuse of the service, violation of these Terms, or
            inability to deliver the service. Upon termination, the data
            handling described in our Privacy Policy applies.
          </p>
        </section>

        <section>
          <h2 style={h2Style}>12. Changes to these Terms</h2>
          <p>
            We may update these Terms from time to time. The
            &ldquo;Last updated&rdquo; date at the top of this page
            reflects the most recent change. Continued use of the app after
            an update constitutes acceptance of the revised Terms.
          </p>
        </section>

        <section>
          <h2 style={h2Style}>13. Governing law</h2>
          <p>
            These Terms are governed by the laws of {GOVERNING_LAW},
            without regard to its conflict-of-law provisions. Any disputes
            arising out of these Terms or the app shall be resolved in the
            courts located in {GOVERNING_LAW}.
          </p>
        </section>

        <section>
          <h2 style={h2Style}>14. Contact</h2>
          <p>
            Questions about these Terms? Email{" "}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
          </p>
        </section>
      </article>
    </main>
  );
}

const pageStyle: React.CSSProperties = {
  minHeight: "100vh",
  backgroundColor: "#f6f6f7",
  padding: "48px 24px",
  fontFamily:
    "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  color: "#202223",
  lineHeight: 1.6,
};

const articleStyle: React.CSSProperties = {
  maxWidth: 760,
  margin: "0 auto",
  backgroundColor: "#ffffff",
  borderRadius: 12,
  padding: "48px 56px",
  boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
};

const h1Style: React.CSSProperties = {
  fontSize: 32,
  fontWeight: 600,
  margin: "0 0 8px",
};

const h2Style: React.CSSProperties = {
  fontSize: 20,
  fontWeight: 600,
  margin: "32px 0 12px",
};

const metaStyle: React.CSSProperties = {
  color: "#6d7175",
  fontSize: 14,
  margin: "0 0 24px",
};
